<template>
    <div class="page-module-list">
        <h3 class="title">{{ mode === "module" ? $t("modules.modules") : $t("modules.relations") }}</h3>
        <div class="table-wrapper">
            <div class="toolbar">
                <div class="left">
                    <a-input-search
                        style="width: 300px"
                        v-model="searchText"
                        :placeholder="$t('message.search')"
                        class="row search-input"
                        @input="onSearch($event.target.value)"
                    />
                </div>
                <div class="right">
                    <SupsisButton primary ghost @click="showCreateModuleModal">{{
                        mode === "module" ? $t("modules.newModule") : $t("modules.newRelation")
                    }}</SupsisButton>
                </div>
            </div>

            <a-spin :spinning="loading">
                <SupsisTable
                    :columns="getCols()"
                    :rows="filteredModules()"
                    :top-pagination="true"
                    empty-data-bordered
                    :bottom-pagination="true"
                    :pagination-cursor="pagination"
                    :options="{
                        rowStyleOption,
                        clipboardOption,
                        fixedHeader: true,
                        id: 'module-table',
                        borderX: true,
                        borderY: true,
                    }"
                    @cursor-changed="(cursor) => (pagination = cursor)"
                />
            </a-spin>
        </div>

        <Modal2 v-if="isCreateModuleModalVisible" @cancel="hideCreateModuleModal" @ok="createModule" xlarge>
            <template #title>{{ $t("modules.newModule") }}</template>
            <CreateModuleForm ref="createModuleForm" @success="onCreateModuleSuccess" :default-mode="mode" />
        </Modal2>
    </div>
</template>

<script>
import Vue from "vue";
import { mapState, mapActions } from "vuex";
import SupsisTable from "../../../components/SupsisTable.vue";
import Modal2 from "../../../components/common/Modal2.vue";
import CreateModuleForm from "./CreateModuleForm.vue";
import SupsisButton from "../Settings/components/SupsisButton.vue";
import { MODULE_API } from "../../../api/module";

export default Vue.extend({
    name: "ModuleList",
    components: {
        SupsisTable,
        Modal2,
        CreateModuleForm,
        SupsisButton,
    },
    props: {
        mode: {
            type: String,
            default: "module",
        },
    },
    data() {
        return {
            searchText: "",
            pagination: {
                page: 1,
                perPage: 20,
                total: 0,
                defaultPageSize: 50,
                hideOnSinglePage: true,
                pageSizeOptions: [10, 15, 20, 50, 100],
                showQuickJumper: true,
                showSizeChanger: true,
            },
            rowStyleOption: {
                stripe: true,
            },
            clipboardOption: {
                copy: true,
                paste: false,
                cut: false,
                delete: false,
            },
            isCreateModuleModalVisible: false,
        };
    },
    computed: {
        ...mapState({
            modules: (state) => state.module.modules,
            loading: (state) => state.module.loading,
            tables: (state) => state.table.tables,
        }),
    },
    methods: {
        ...mapActions({
            syncModules: "module/syncModules",
        }),
        getCols() {
            return [
                {
                    key: "displayName",
                    field: "displayName",
                    title: this.$t("modules.displayName"),
                    width: "",
                    align: "left",
                },
                this.mode === "module" && {
                    key: "moduleName",
                    field: "moduleName",
                    title: this.$t("modules.moduleName"),
                    width: "",
                    align: "left",
                },
                this.mode === "module" && {
                    key: "apiName",
                    field: "apiName",
                    title: this.$t("modules.apiName"),
                    width: "",
                    align: "left",
                },
                this.mode === "module" && {
                    key: "type",
                    field: "type",
                    title: this.$t("modules.type"),
                    width: "",
                    align: "left",
                },
                this.mode === "relation" && {
                    key: "relationType",
                    field: "relation.type",
                    title: this.$t("modules.relationType"),
                    width: "",
                    align: "left",
                    renderBodyCell: ({ row }, h) => {
                        const typeMap = {
                            "table-to-table": "Tablodan Tabloya",
                            "table-to-standart": "Tablodan Standart Modüle",
                            "standart-to-table": "Standart Modülden Tabloya"
                        };
                        return <span>{typeMap[row.relation?.type]}</span>;
                    }
                },
                this.mode === "relation" && {
                    key: "from",
                    field: "from",
                    title: "From",
                    width: "",
                    align: "left",
                    renderBodyCell: ({ row }, h) => {
                        if (row.relation?.tableToTable) {
                            const fromTable = row.relation.tableToTable.from;
                            const table = this.tables.find(t => t._id === fromTable.table);
                            const tableName = table?.name || '-';
                            const fieldName = fromTable.connectionType === '_id' ? '_id' : 
                                table?.fields?.find(f => f._id === fromTable.field)?.name || '-';
                            return <span>{tableName} ({fieldName})</span>;
                        } else if (row.relation?.standartToTable) {
                            const fromStandart = row.relation.standartToTable.from;
                            return <span>{fromStandart.module} ({fromStandart.field})</span>;
                        } else if (row.relation?.tableToStandart) {
                            const fromTable = row.relation.tableToStandart.from;
                            const table = this.tables.find(t => t._id === fromTable.table);
                            const tableName = table?.name || '-';
                            return <span>{tableName} ({fromTable.field})</span>;
                        }
                        return <span>-</span>;
                    }
                },
                this.mode === "relation" && {
                    key: "to",
                    field: "to",
                    title: "To",
                    width: "",
                    align: "left",
                    renderBodyCell: ({ row }, h) => {
                        if (row.relation?.tableToTable) {
                            const toTable = row.relation.tableToTable.to;
                            const table = this.tables.find(t => t._id === toTable.table);
                            const tableName = table?.name || '-';
                            const fieldName = toTable.connectionType === '_id' ? '_id' : 
                                table?.fields?.find(f => f._id === toTable.field)?.name || '-';
                            return <span>{tableName} ({fieldName})</span>;
                        } else if (row.relation?.standartToTable) {
                            const toTable = row.relation.standartToTable.to;
                            const table = this.tables.find(t => t._id === toTable.table);
                            const tableName = table?.name || '-';
                            const fieldName = toTable.connectionType === '_id' ? '_id' : 
                                table?.fields?.find(f => f._id === toTable.field)?.name || '-';
                            return <span>{tableName} ({fieldName})</span>;
                        } else if (row.relation?.tableToStandart) {
                            const toStandart = row.relation.tableToStandart.to;
                            return <span>{toStandart.module} ({toStandart.field})</span>;
                        }
                        return <span>-</span>;
                    }
                },
                {
                    key: "description",
                    field: "description",
                    title: this.$t("modules.description"),
                    width: "",
                    align: "left",
                },
                this.mode === "module"  &&
                {
                    key: "module.settings.showInMainMenu",
                    field: "module.settings.showInMainMenu",
                    title: this.$t("modules.showInMainMenu"),
                    width: "",
                    align: "center",
                    renderBodyCell: ({ row }, h) => {
                        return <a-icon type={row.module?.settings?.showInMainMenu ? "check" : "close"} />;
                    },
                },
                this.mode === "module"  &&
                {
                    key: "module.settings.apiEnabled",
                    field: "module.settings.apiEnabled",
                    title: this.$t("modules.apiAccess"),
                    width: "",
                    align: "center",
                    renderBodyCell: ({ row }, h) => {
                        return <a-icon type={row.module?.settings?.apiEnabled ? "check" : "close"} />;
                    },
                },
                
                {
                    key: "actions",
                    field: "actions",
                    title: this.$t("modules.actions"),
                    width: "",
                    align: "center",
                    renderBodyCell: ({ row }, h) => {
                        return (
                            <div class="actions d-flex gap-5">
                                <SupsisButton ghost icon="edit" xsmall />
                                <SupsisButton
                                    danger
                                    ghost
                                    icon="delete"
                                    onClick={() => this.deleteModule(row._id)}
                                    xsmall
                                />
                            </div>
                        );
                    },
                },
            ].filter(Boolean);
        },
        onSearch(value) {
            this.searchText = value;
        },
        filteredModules() {
            const modules = this.modules.filter((module) => module.type === this.mode);
            if (!this.searchTex) {
                return modules;
            }
            if (this.searchText.trim().length === 0) {
                return modules;
            }
            return modules.filter(
                (module) =>
                    module.displayName.includes(this.searchText) ||
                    module.moduleName.includes(this.searchText) ||
                    module.apiName.includes(this.searchText)
            );
        },
        showCreateModuleModal() {
            this.isCreateModuleModalVisible = true;
        },
        hideCreateModuleModal() {
            this.isCreateModuleModalVisible = false;
        },
        async createModule() {
            await this.$refs.createModuleForm.handleSubmit();
        },
        onCreateModuleSuccess() {
            this.hideCreateModuleModal();
        },
        async deleteModule(moduleId) {
            try {
                await this.$confirm({
                    title: this.$t("modules.deleteModuleTitle"),
                    content: this.$t("modules.deleteModuleContent"),
                    okText: this.$t("calendar.yes"),
                    okType: "danger",
                    cancelText: this.$t("calendar.no"),
                    onOk: async () => {
                        await MODULE_API.deleteModule(moduleId);
                        this.syncModules();
                        this.$message.success(this.$t("modules.moduleDeletedSuccessfully"));
                    },
                });
            } catch (error) {
                console.error("Modül silinirken hata oluştu:", error);
            }
        },
    },
    mounted() {
        this.syncModules();
    },
    watch: {
        pagination: {
            handler(val, oldVal) {
                const pageSizeChanged = val.perPage !== oldVal.perPage;
                if (pageSizeChanged) {
                    this.pagination.page = 1;
                }
                this.syncModules();
            },
            deep: true,
        },
    },
});
</script>

<style scoped lang="scss">
.page-module-list {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 10px;
    width: 100%;

    .title {
        font-size: 18px;
        font-weight: bold;
    }

    .table-wrapper {
        display: flex;
        flex-direction: column;
        gap: 5px;

        .toolbar {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 0;
            gap: 5px;

            .left {
                display: flex;
                gap: 5px;
                align-items: center;
            }

            .right {
                display: flex;
                gap: 5px;
                align-items: center;
            }
        }
    }
}

.actions {
    display: flex;
    gap: 5px;
}

.actions .supsis-button {
    flex-grow: 1;
    text-align: center;
}
</style>
